/* eslint-disable react/no-unused-prop-types */
import { memo } from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldError,
} from 'react-hook-form';
import ReSelect, { SingleValue } from 'react-select';
import styled from 'styled-components';
import cx from 'classnames';

import { Fields, Names } from 'types/fields';
import Text from '../text';
import {
  TextFieldError,
  TextFieldInput,
  TextFieldLabel,
} from 'components/common/text-field';
import { device } from 'styles/breakpoints';

export type SelectProps = {
  control?: Control<Fields, any>;
  name: Names;
  label?: string;
  error?: FieldError;
  initialValue?: string;
};

const options = [
  { value: 'buy', label: 'Buyer' },
  { value: 'sell', label: 'Seller' },
];

function Select({ label, name, control, error, initialValue }: SelectProps) {
  return (
    <SelectWrapper>
      {label && (
        <TextFieldLabel data-testid="select-label">
          <label htmlFor={name}>
            <Text type="mode2" fw="light">
              {label}
            </Text>
          </label>
        </TextFieldLabel>
      )}
      <TextFieldInput>
        <Controller
          defaultValue={initialValue}
          control={control}
          name={name}
          render={textController(error)}
        />
      </TextFieldInput>
      <TextFieldError data-testid="select-error">
        {error?.message && (
          <Text type="mode2" fw="medium">
            {error.message}
          </Text>
        )}
      </TextFieldError>
    </SelectWrapper>
  );
}

function textController(error: FieldError | undefined) {
  return function renderTextController({
    field: { onChange, value, onBlur },
  }: RenderTextControllerProps) {
    function handleChange(
      option: SingleValue<{
        value: string;
        label: string;
      }>
    ) {
      onChange(option?.value);
    }

    return (
      <ReSelect
        options={options}
        placeholder="Choose as option"
        onChange={handleChange}
        onBlur={onBlur}
        value={options.filter((option) => value?.includes(option.value))}
        className={cx('select-container', { invalid: error })}
      />
    );
  };
}

interface RenderTextControllerProps {
  field: ControllerRenderProps<Fields>;
}

export const SelectWrapper = styled.div`
  width: 100%;
  margin-top: 24px;

  .select-container {
    font-family: 'Walsheim-Regular';
    &.invalid {
      .css-1s2u09g-control {
        border-color: ${({ theme }) => theme.colors.red};
      }
    }
  }
  .css-1s2u09g-control,
  .css-1pahdxg-control {
    border-radius: unset;
    border-width: 1px;
    min-height: 55px;
    box-shadow: unset;
  }

  &.styled-select {
    .select-label {
      margin-bottom: 10px;

      p {
        color: ${({ theme }) => theme.colors.blackPallet6};
      }
    }

    .select-container {
      font-family: 'Inter', sans-serif;
    }

    .react-select__control {
      height: 55px;
      border: 1px solid ${({ theme }) => theme.colors.grayPallet19};
      border-radius: 0;
      background-color: ${({ theme }) => theme.colors.whitePallet3};
    }

    .react-select__indicator-separator {
      display: none;
    }

    .react-select__indicator {
      color: ${({ theme }) => theme.colors.blackPallet6};
      padding: 16px;
    }

    .react-select__placeholder {
      color: ${({ theme }) => theme.colors.grayPallet18};
    }

    .react-select__value-container {
      padding: 13px 20px;
    }

    .react-select_ {
      &_option {
        font-family: 'Inter', sans-serif;
      }

      &_option--is-selected {
        background: ${({ theme }) => theme.colors.grayPallet19};
        color: ${({ theme }) => theme.colors.blackPallet6};
      }

      &_option:hover,
      &_option--is-focused {
        background: ${({ theme }) => theme.colors.grayPallet19};
      }

      &_control--menu-is-open,
      &_control--is-focused,
      &_control--is-focused:hover,
      &_control--menu-is-open:hover {
        box-shadow: none;
        border: 1px solid ${({ theme }) => theme.colors.green};
      }
    }

    @media ${device.tablet} {
      .react-select__control {
        height: 51px;
      }

      .react-select__value-container {
        padding: 12px 15px;
      }
    }
  }
`;

export default memo(Select);
